import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { carryPadModes } from '../utils/chartIntensity';

/**
 * A song's charts in playing order, so the chart player can carry on from the part it was opened on
 * to the end of the song.
 *
 * Each part hands in its own chart — as the player would be given it — rather than this reading
 * the song: the words, the melody's transposition and the tempo are all worked out in ViewSongPart
 * against the reader's settings, and working them out a second time here would be a second answer.
 * Read when Play through is switched on, not on every render.
 */
const ChartRunContext = createContext(null);

/**
 * @param {string[]} partIds - The song's parts as they are rendered, in order. One song: a run stops
 *   at the end of it rather than crossing into the next song of a service.
 */
export const ChartRunProvider = ({ partIds, children }) => {
  const entriesRef = useRef(new Map());
  const partIdsRef = useRef(partIds);
  partIdsRef.current = partIds;

  const value = useMemo(
    () => ({
      register: (partId, getEntry) => {
        entriesRef.current.set(partId, getEntry);

        return () => {
          if (entriesRef.current.get(partId) === getEntry) entriesRef.current.delete(partId);
        };
      },
      // The parts from this one to the end of the song: `played`, those with a chart to play, and
      // `shown`, what the run lists. Where every part left to play has its chart, that is the song as
      // the page shows it — notes and section titles too, passed over when it plays. Otherwise only
      // the charted parts, so a gap in the charts doesn't read as a part being skipped.
      getRunFrom: (partId) => {
        const ids = partIdsRef.current;
        const from = ids.indexOf(partId);

        if (from < 0) return { played: [], shown: [] };

        // Read from the top, so a pad kept on before this part is still on at it
        const items = carryPadModes(
          ids.map((id) => entriesRef.current.get(id)?.current?.() ?? null).filter(Boolean),
        ).filter((item) => ids.indexOf(item.partId) >= from);
        const played = items.filter((item) => item.lines);
        const isFullyCharted = items.every((item) => item.lines || !item.needsChart);

        return { played, shown: isFullyCharted ? items : played };
      },
    }),
    [],
  );

  return <ChartRunContext.Provider value={value}>{children}</ChartRunContext.Provider>;
};

/**
 * Hands a part's chart to the run. Returns a ref for the part to point at a function returning its
 * entry — `{ partId, title, name, number, lines, sections, repeatCount, bpm, instrumentId, rhythmChoice,
 * melody, lyricsHtml, notesHtml }`, `rhythmChoice` as getChartRhythm gives it. A part with nothing to play returns one without `lines`, for the run to
 * show: `needsChart` where it is a part the song isn't fully charted without, `section` for a
 * section title. Left at null by a part that isn't on the page. A ref rather than a value, so the part
 * can set it after its early returns and the run reads it as it is when asked.
 */
export const useChartRunEntry = (partId) => {
  const context = useContext(ChartRunContext);
  const getEntry = useRef(null);

  useEffect(() => context?.register(partId, getEntry), [context, partId]);

  return getEntry;
};

/** The run's `getRunFrom`, or null outside a song — the builder, a printed sheet. */
export const useChartRun = () => useContext(ChartRunContext)?.getRunFrom ?? null;
